import { Link } from 'react-router-dom';
import CategoryBadge from '@/components/base/CategoryBadge';
import type { Topic } from '@/mocks/topics';

interface TopicCardProps {
  topic: Topic;
  layout?: 'vertical' | 'horizontal';
}

export default function TopicCard({ topic, layout = 'vertical' }: TopicCardProps) {
  if (layout === 'horizontal') {
    return (
      <article className="group grid grid-cols-1 gap-4 rounded-lg border border-background-300 bg-background-100 p-4 transition-colors hover:border-primary-300 sm:grid-cols-[200px_1fr]">
        <Link
          to={`/topics/${topic.slug}`}
          className="block h-40 w-full overflow-hidden rounded-md bg-background-200 sm:h-full sm:w-[200px]"
        >
          <img
            src={topic.cover}
            alt={`${topic.title} - 社会爆料议题配图`}
            title={`${topic.title} - 社会爆料`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <CategoryBadge category={topic.category} size="sm" />
            <span className="font-label text-xs text-foreground-500">{topic.date}</span>
          </div>
          <h3 className="mt-2 font-heading text-lg font-semibold leading-snug text-foreground-950">
            <Link to={`/topics/${topic.slug}`} className="transition-colors hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {topic.summary}
          </p>
          <div className="mt-auto flex items-center gap-4 pt-3 font-label text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" aria-hidden="true"></i>
              {topic.readTime} 分钟
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {topic.heat.toLocaleString('zh-CN')}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-300 bg-background-50 transition-colors hover:border-primary-300">
      <Link
        to={`/topics/${topic.slug}`}
        className="block h-48 w-full overflow-hidden bg-background-200"
      >
        <img
          src={topic.cover}
          alt={`${topic.title} - 社会爆料议题配图`}
          title={`${topic.title} - 社会爆料`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2">
          <CategoryBadge category={topic.category} size="sm" />
          <span className="font-label text-xs text-foreground-500">{topic.date}</span>
        </div>
        <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-foreground-950">
          <Link to={`/topics/${topic.slug}`} className="transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {topic.summary}
        </p>
        <div className="mt-auto flex items-center justify-between pt-4 font-label text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line" aria-hidden="true"></i>
            {topic.readTime} 分钟
          </span>
          <span className="inline-flex items-center gap-1 text-accent-700">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {topic.heat.toLocaleString('zh-CN')}
          </span>
        </div>
      </div>
    </article>
  );
}